import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  InlineStack,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Modal,
  TextField,
  Text,
  Box,
  Banner,
} from '@shopify/polaris';
import { consignmentApi } from '@/services/consignment';
import { clinicsApi } from '@/services/clinics';
import { assetsApi } from '@/services/assets';
import SearchableSelect from '@/components/SearchableSelect';
import type { ConsignmentSummary } from '@/services/consignment';

type BadgeTone = 'info' | 'success' | 'critical' | undefined;

const STATUS_BADGE_MAP: Record<string, { label: string; tone: BadgeTone }> = {
  at_clinic: { label: 'At Clinic', tone: 'info' },
  fulfilled: { label: 'Fulfilled', tone: 'success' },
  returned_to_warehouse: { label: 'Returned', tone: undefined },
  lost: { label: 'Lost', tone: 'critical' },
};

export default function ConsignmentInventory() {
  const queryClient = useQueryClient();

  // Modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    clinicId: '',
    serialNumber: '',
    productSku: '',
    notes: '',
  });
  const [assignError, setAssignError] = useState('');

  // Fetch summary
  const {
    data: summary,
    isLoading,
    error,
  } = useQuery<ConsignmentSummary>({
    queryKey: ['consignment-summary'],
    queryFn: () => consignmentApi.getSummary(),
  });

  // Clinics search on the server. The previous capped fetch put roughly a quarter of
  // the 842 clinics into a native Select, with no way to type or reach the rest.
  const [assignClinicName, setAssignClinicName] = useState('');
  const searchClinics = useCallback(async (query: string) => {
    const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
    return res.data.map((c) => ({
      label: c.name,
      value: c.id,
      detail: c.email || undefined,
    }));
  }, []);

  // Mutations
  const assignMutation = useMutation({
    mutationFn: async () => {
      // Look up asset by serial number
      const asset = await assetsApi.getBySerial(assignForm.serialNumber);
      return consignmentApi.assign({
        clinicId: assignForm.clinicId,
        assetId: asset.id,
        productSku: assignForm.productSku || asset.productSku,
        productName: asset.productName || undefined,
        serialNumber: assignForm.serialNumber,
        notes: assignForm.notes || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consignment-summary'] });
      setAssignModalOpen(false);
      resetAssignForm();
    },
    onError: (err: any) => {
      setAssignError(
        err?.response?.data?.message || err?.message || 'Failed to assign asset',
      );
    },
  });

  const returnMutation = useMutation({
    mutationFn: (id: string) => consignmentApi.markReturned(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['consignment-summary'] }),
  });

  const lostMutation = useMutation({
    mutationFn: (id: string) => consignmentApi.markLost(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['consignment-summary'] }),
  });

  const resetAssignForm = useCallback(() => {
    setAssignForm({ clinicId: '', serialNumber: '', productSku: '', notes: '' });
    setAssignError('');
  }, []);

  const handleAssignModalClose = useCallback(() => {
    setAssignModalOpen(false);
    resetAssignForm();
  }, [resetAssignForm]);

  // Build table rows from summary
  const rows: (string | React.ReactNode)[][] = [];
  if (summary) {
    for (const clinic of summary.clinics) {
      for (const item of clinic.items) {
        const badge = STATUS_BADGE_MAP['at_clinic'];
        rows.push([
          clinic.clinicName,
          item.serialNumber,
          item.productSku,
          item.productName || '-',
          <Badge tone={badge.tone}>{badge.label}</Badge>,
          new Date(item.assignedAt).toLocaleDateString(),
          <InlineStack gap="200">
            <Button
              size="slim"
              onClick={() => returnMutation.mutate(item.id)}
              loading={returnMutation.isPending}
            >
              Return
            </Button>
            <Button
              size="slim"
              tone="critical"
              onClick={() => lostMutation.mutate(item.id)}
              loading={lostMutation.isPending}
            >
              Lost
            </Button>
          </InlineStack>,
        ]);
      }
    }
  }


  if (isLoading) {
    return (
      <Page title="Consignment Inventory">
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Consignment Inventory">
        <Banner tone="critical">
          <p>Failed to load consignment data.</p>
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Consignment Inventory"
      primaryAction={{
        content: 'Backfill Existing Stock',
        onAction: () => setAssignModalOpen(true),
      }}
    >
      <BlockStack gap="400">
        <Banner tone="info">
          <p>
            Consignment inventory is created automatically when a replenishment
            shipment to a consignment clinic is dispatched from the warehouse.
            Use <strong>Backfill Existing Stock</strong> only to record equipment
            that the clinic already has on-site from prior arrangements.
          </p>
        </Banner>

        {/* Summary cards */}
        <InlineStack gap="400" wrap={false}>
          <Box
            background="bg-surface"
            padding="400"
            borderRadius="200"
            borderWidth="025"
            borderColor="border"
            minWidth="200px"
          >
            <BlockStack gap="100">
              <Text as="p" variant="bodySm" tone="subdued">
                Clinics with Consignment
              </Text>
              <Text as="p" variant="headingLg">
                {summary?.totalClinics ?? 0}
              </Text>
            </BlockStack>
          </Box>
          <Box
            background="bg-surface"
            padding="400"
            borderRadius="200"
            borderWidth="025"
            borderColor="border"
            minWidth="200px"
          >
            <BlockStack gap="100">
              <Text as="p" variant="bodySm" tone="subdued">
                Total Units Out
              </Text>
              <Text as="p" variant="headingLg">
                {summary?.totalUnits ?? 0}
              </Text>
            </BlockStack>
          </Box>
        </InlineStack>

        {/* Data table */}
        <Card>
          {rows.length === 0 ? (
            <EmptyState
              heading="No consignment inventory"
              image=""
            >
              <p>
                Assign equipment to clinics so they can fulfill patient orders
                directly from their local stock.
              </p>
            </EmptyState>
          ) : (
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
              ]}
              headings={[
                'Clinic',
                'Serial Number',
                'Product SKU',
                'Product Name',
                'Status',
                'Assigned Date',
                'Actions',
              ]}
              rows={rows}
            />
          )}
        </Card>
      </BlockStack>

      {/* Assign Modal */}
      <Modal
        open={assignModalOpen}
        onClose={handleAssignModalClose}
        title="Backfill Existing Stock"
        primaryAction={{
          content: 'Backfill',
          onAction: () => assignMutation.mutate(),
          loading: assignMutation.isPending,
          disabled: !assignForm.clinicId || !assignForm.serialNumber,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleAssignModalClose,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {assignError && (
              <Banner tone="critical" onDismiss={() => setAssignError('')}>
                <p>{assignError}</p>
              </Banner>
            )}
            <SearchableSelect
              label="Clinic"
              value={assignForm.clinicId}
              selectedLabel={assignClinicName}
              onChange={(val, option) => {
                setAssignForm((prev) => ({ ...prev, clinicId: val }));
                setAssignClinicName(option?.label ?? '');
              }}
              onSearch={searchClinics}
              emptyOption={{ label: 'Select a clinic', value: '' }}
              placeholder="Type a clinic name…"
            />
            <TextField
              label="Asset Serial Number"
              value={assignForm.serialNumber}
              onChange={(val) =>
                setAssignForm((prev) => ({ ...prev, serialNumber: val }))
              }
              autoComplete="off"
            />
            <TextField
              label="Product SKU"
              value={assignForm.productSku}
              onChange={(val) =>
                setAssignForm((prev) => ({ ...prev, productSku: val }))
              }
              helpText="Leave blank to use asset's SKU"
              autoComplete="off"
            />
            <TextField
              label="Notes"
              value={assignForm.notes}
              onChange={(val) =>
                setAssignForm((prev) => ({ ...prev, notes: val }))
              }
              multiline={3}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
